import { useEffect } from "react";

// Canonical/OG always point at the production domain regardless of environment.
const SITE_ORIGIN = "https://triptrixvoyages.co.in";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-card.png`;

// Central guard: meta descriptions never exceed ~160 chars (Google truncates
// beyond that). Cuts at a sentence/phrase boundary, never mid-word.
const trimDesc = (s = "") => {
  const t = String(s).trim();
  if (t.length <= 160) return t;
  const cut = t.slice(0, 158);
  const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("; "), cut.lastIndexOf(" — "));
  if (stop > 90) return cut.slice(0, stop + 1);
  return cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:\s—-]+$/, "") + "…";
};
const SITE_NAME = "TripTrix Voyages";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(href) {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

const DEFAULT_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

// Per-page SEO: title, description, keywords, canonical, Open Graph & Twitter cards.
export default function usePageMeta({ title, description, image, type = "website", keywords, publishedTime, modifiedTime, robots }) {
  useEffect(() => {
    const url = SITE_ORIGIN + window.location.pathname;
    const ogImage = image || DEFAULT_OG_IMAGE;
    const desc = trimDesc(description);

    if (title) document.title = title;
    upsertMeta("name", "description", desc);
    upsertMeta("name", "keywords", keywords);
    // Always reset robots so a noindex page never sticks when navigating on (SPA).
    upsertMeta("name", "robots", robots || DEFAULT_ROBOTS);
    upsertCanonical(url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", `${SITE_NAME} — ${title || "Himalayan DMC"}`);
    upsertMeta("property", "og:site_name", SITE_NAME);
    if (type === "article" && publishedTime) upsertMeta("property", "article:published_time", publishedTime);
    if (type === "article") upsertMeta("property", "article:modified_time", modifiedTime || publishedTime);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", ogImage);

    window.scrollTo(0, 0);
  }, [title, description, image, type, keywords, publishedTime, modifiedTime, robots]);
}
